import React from 'react';
import classnames from 'classnames';
import { Button } from 'new/elements/Button';
import MarkdownEditor from 'new/components/LexicalMarkdownEditor';
import { GenAiButton } from 'js/components/elements/GenAiButton';
import { RESOURCE_TYPE_FLOW } from 'js/constants/resourceTypes';
import { ModalCompare } from 'js/components/elements/editors/ModalCompare';
import { ModalDefault } from 'new/routes/ModalDefault';
import DocEditorSkeleton from './DocEditorSkeleton';
import { connect } from 'react-redux';
import { DOC_RECOMMENDATION_REQUEST } from 'js/constants/actionTypes';
import { getDocRecommendation } from 'js/actions/recommendations';
import some from 'lodash/some';

import s from './DocEditor.module.scss';

class DocEditor extends React.Component {
  constructor(props) {
    super(props);

    const docContent = props.doc || '';

    this.state = {
      docContent, // updates as the user types into the editor
      savedDocContent: { value: docContent }, // updates when changes are saved or discarded
      isEditingDoc: props.defaultEdit || false,
      showDiff: false,
    };
  }

  componentDidUpdate(prevProps) {
    const { doc } = this.props;

    // Sync external doc changes (late API load, discard, GenAI accept) into state.
    if (prevProps.doc !== doc) {
      const docContent = doc || '';
      this.setState({ docContent, savedDocContent: { value: docContent } });
    }
  }

  render() {
    const {
      isLoading,
      hideActions,
      readOnly,
      loadingRecommendations,
      useGenAi,
      standalone,
      resourceType,
    } = this.props;
    const { savedDocContent, isEditingDoc } = this.state;

    if (isLoading) {
      return <DocEditorSkeleton />;
    }

    const canEdit = !readOnly;

    const aiResourceId =
      resourceType === RESOURCE_TYPE_FLOW ? this.props.data?.origin_node_id : this.props.data?.id;
    // One prominent "Generate" affordance in the header (always accessible), plus a
    // "/ai" slash entry — both wired to the same getDocRecommendation flow. The
    // GenAiButton owns gating (disabled + "enable it" hint when GenAI is off),
    // loading, and inserting the result via onAccept.
    const generateButton =
      canEdit && useGenAi ? (
        <span data-testid="doc-generate-ai">
          <GenAiButton
            label="Generate"
            description="Create Documentation with Generative AI"
            onAccept={this.onAcceptRecommendation}
            id={aiResourceId}
            resourceType={resourceType}
          />
        </span>
      ) : null;
    const aiSlash = useGenAi
      ? {
          available: !!this.props.genAiAvailable,
          onGenerate: () =>
            this.props.dispatch(getDocRecommendation({ id: aiResourceId, resourceType })),
        }
      : undefined;

    // The quiet uppercase field-label shown on the left of the bar in read
    // mode. In edit mode the editor swaps in its format controls here, so the
    // section title never competes with the document's own headings.
    const barLabel = (
      <span className="doc-bar-label">
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <path d="M14 2v6h6" />
        </svg>
        <span>Documentation</span>
      </span>
    );

    // Dirty = the working text differs from what's persisted. Drives the subtle
    // save-status indicator and gates Save / View changes.
    const dirty = (this.state.docContent || '') !== (this.props.doc || '');
    const saveStatus = loadingRecommendations
      ? 'Generating…'
      : dirty
        ? 'Unsaved changes'
        : 'All changes saved';

    const placeholder = !canEdit
      ? 'No documentation'
      : useGenAi
        ? 'Add a description, or generate one with AI…'
        : 'Add a description…';

    return (
      <section className={s.container}>
        <div className="detail-row">
          <div className="detail-col">
            <div
              id="doc-editor-container"
              className={classnames({
                'is-editing': isEditingDoc,
                'read-only': readOnly || false,
                'doc-disabled': loadingRecommendations,
                standalone: standalone || false,
              })}
              onClick={this._handleContainerClick}
            >
              <MarkdownEditor
                placeholder={placeholder}
                committedValue={savedDocContent}
                onChange={this._handleDocContentChanged}
                readOnly={readOnly || !isEditingDoc}
                chromeless
                barLabel={barLabel}
                barAction={generateButton}
                aiSlash={aiSlash}
              />

              {isEditingDoc && !hideActions && (
                <div
                  className={classnames('doc-action-bar', {
                    'is-dirty': dirty,
                    'is-generating': loadingRecommendations,
                  })}
                >
                  <div className="doc-save-status">
                    <span className="doc-save-dot" />
                    <span>{saveStatus}</span>
                  </div>
                  <div className="doc-action-buttons">
                    <Button
                      variant="text"
                      color="primary"
                      // Nothing to compare until the text diverges from the
                      // saved doc — avoids an empty Before/After diff.
                      disabled={loadingRecommendations || !dirty}
                      onClick={this.onClickShowDiff}
                    >
                      View changes
                    </Button>
                    <Button
                      variant="outlined"
                      color="neutral"
                      disabled={loadingRecommendations}
                      onClick={this._handleDiscardDocContentChanges}
                    >
                      <span>Discard</span>
                    </Button>
                    <Button
                      color="primary"
                      variant="contained"
                      disabled={loadingRecommendations || !dirty}
                      onClick={this._handleSaveDocContent}
                    >
                      <span>Save</span>
                    </Button>
                  </div>
                </div>
              )}

              <ModalDefault
                open={this.state.showDiff}
                title="Do you want to accept changes?"
                onCancel={this.onCloseDiff}
              >
                <ModalCompare
                  original={this.props.doc || ''}
                  modified={this.state.docContent || ''}
                  onSave={this._handleSaveDocContent}
                  onCancel={this._handleDiscardDocContentChanges}
                />
              </ModalDefault>
            </div>
          </div>
        </div>
      </section>
    );
  }

  onCloseDiff = () => {
    this.setState({
      showDiff: false,
    });
  };
  onClickShowDiff = () => {
    this.setState({
      showDiff: true,
    });
  };

  onAcceptRecommendation = (value) => {
    if (!this.props.readOnly) {
      this.setState({
        isEditingDoc: true,
        savedDocContent: { value },
      });
    }
    this._handleDocContentChanged(value);
  };

  // Click the content area to start editing (inline; no separate Edit button).
  _handleContainerClick = () => {
    if (!this.props.readOnly && !this.state.isEditingDoc) {
      this._handleEditDocContentClicked();
    }
  };

  _handleEditDocContentClicked = () => {
    if (!this.props.readOnly) {
      this.setState({
        isEditingDoc: true,
      });
    }
  };

  _handleDocContentChanged = (value) => {
    if (this.props.onDocContentChange) {
      this.props.onDocContentChange(value);
    }
    this.setState({ docContent: value });
  };

  _handleDiscardDocContentChanges = () => {
    const { doc } = this.props;
    const docContent = doc || '';

    this.setState({
      docContent,
      showDiff: false,
      savedDocContent: { value: docContent },
      isEditingDoc: false,
    });
  };

  _handleSaveDocContent = (newContent) => {
    const { data, updateDoc } = this.props;
    const { docContent } = this.state;

    const content = !!newContent && typeof newContent === 'string' ? newContent : docContent;

    updateDoc(data.id, content);
    this.setState({
      showDiff: false,
      docContent: content,
      savedDocContent: { value: content },
      isEditingDoc: false,
    });
  };
}

const mapStateToProps = (state) => {
  return {
    loadingRecommendations: !!state.request[DOC_RECOMMENDATION_REQUEST],
    genAiAvailable: some(state.entities?.genAiConfigs, (c) => c?.status === 'active'),
  };
};

export default connect(mapStateToProps)(DocEditor);
